import React, { useState } from 'react'
import Layout from 'Layout'
import './styles.scss'
import TradingChart from './TradingChart'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'antd'
import { mixGetV2 } from 'services/mixin'
import { useTranslation } from 'react-i18next'

type EquityCurveProps = {
    avg_equity: number;
    line1: number;
    line2: number;
    line3: number;
    line4: number;
    line5: number;
    line6: number;
    line7: number;
    line8: number;
    line9: number;
    line10: number;
    trade: number;
};

const Dashboard: React.FC = () => {
    const navigate = useNavigate()
    const { t } = useTranslation();
    const [numberAccount, getNumberAccount] = useState('')
    const [isShowRun, setIsShowRun] = useState(false)
    const [avgDataTable, setAvgDataTable] = useState<{
        avg_drawdown_percent: number
        max_consecutive_losers: number
        max_consecutive_winners: number
        max_drawdown_percent: number
        max_equity: number
        min_equity: number
    }>()
    const [equityCurves, setEquityCurves] = useState<EquityCurveProps[]>()

    const saveLocal = () => {
        localStorage.setItem('account_number', numberAccount)
        navigate('/account-live-metrix')
    }
    // State cho từng input
    const [capital, setCapital] = useState(50000)
    const [win, setWin] = useState(50)
    const [rrr, setRRR] = useState(1)
    const [iterations, setIterations] = useState(100)
    const [lines, setLines] = useState(10)
    const [risk, setRisk] = useState(10)

    // Hàm tăng/giảm
    const handleChange = (setter: React.Dispatch<React.SetStateAction<number>>, value: number, min: number, max: number, step = 1) => {
        setter((prev) => {
            let next = prev + value * step
            if (next < min) next = min
            if (next > max) next = max
            return Number(next.toFixed(8))
        })
    }

    const sendData = async () => {
        const res = await mixGetV2(`/tool/simulator-matrix-rr-risk?matrix_balance_size=10&matrix_number_trades=10&matrix_rr=10&matrix_list_winrate=0.4%2C%200.5%2C%200.6%2C%200.7&matrix_list_risk_percent=0.002%2C%200.0025%2C%200.003%2C%200.0035%2C%200.004&chart_balance_size=${capital}&chart_lines=${lines}&chart_number_trades=${iterations}&chart_winrate=${win / 100}&chart_rr=${rrr}&chart_risk_percent=${risk / 100}`)

        if (res) {
            setIsShowRun(true)
            setAvgDataTable(res.analyze_equity_curves.avg_equity)
            setEquityCurves(res.equity_curves)
        }
    }

    return (
        <Layout>
            <nav className="breadcrumb__container mb-3">
                <div className="breadcrumb__gradient breadcrumb__gradient--left" data-gradient="left" style={{ display: 'none' }}></div>
                <ul className="breadcrumb" data-breadcrumb-scroll-container="">
                    <li className="breadcrumb-item"><a href="/accounts-overview">Trader</a></li>
                    <li className="breadcrumb-item active">
                        {t('EquitySimulator')}
                    </li>
                </ul>
                <div className="breadcrumb__gradient breadcrumb__gradient--right" data-gradient="right" style={{ display: 'none' }}></div>
            </nav>

            <div className="section-header mb-4">
                <div className="section-title">
                    <div className="section-title__title-wrap">
                        <div className="section-title--main">{t('EquitySimulator')}</div>
                    </div>
                    <p className='!text-sm !font-medium !mb-0'>{t('EquitySimulatorDesc')}</p>
                </div>
            </div>

            <div className="portlet">
                <div className="portlet__body pt-0">
                    <form id="equity_simulator_form" action="#" method="post">
                        <div className="row">
                            {/* Capital */}
                            <div className="col-12 col-md-4">
                                <Tooltip title={t('tooltip_CapitalInput')}>
                                    <label htmlFor="capital" className="col-form-label">
                                        <span>{t("Capital")}</span>
                                        <i className="bi bi-info-circle ms-1"></i>
                                    </label>
                                </Tooltip>
                                <div className="number-input__parent">
                                    <span
                                        className="number-input__arrow number-input__before"
                                        onClick={() => handleChange(setCapital, -1, 1, 100000000, 1000)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                    <input
                                        className="form-control text-success number-input__input"
                                        value={capital.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        data-decimals="2"
                                        onChange={e => setCapital(Number(e.target.value.replace(/,/g, '')))}
                                    />
                                    <input
                                        name="capital"
                                        className="number-input__hidden"
                                        value={capital}
                                        type="number"
                                        min="1"
                                        max="100000000"
                                        step="any"
                                    />
                                    <span
                                        className="number-input__arrow number-input__after"
                                        onClick={() => handleChange(setCapital, 1, 1, 100000000, 1000)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                </div>
                                <div className="invalid-feedback">{t('min')}. 1</div>
                            </div>
                            {/* Win rate */}
                            <div className="col-12 col-md-4 mt-2 mt-md-0">
                                <Tooltip title={t('tooltip_winrateInput')}>
                                    <label htmlFor="win" className="col-form-label">
                                        <span>{t('winrate')}</span>
                                        <i className="bi bi-info-circle ms-1"></i>
                                    </label>
                                </Tooltip>
                                <div className="number-input__parent">
                                    <span
                                        className="number-input__arrow number-input__before"
                                        onClick={() => handleChange(setWin, -1, 0, 100)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                    <input
                                        className="form-control text-success number-input__input"
                                        value={win}
                                        data-decimals="0"
                                        onChange={e => setWin(Number(e.target.value))}
                                    />
                                    <input
                                        name="win"
                                        className="number-input__hidden"
                                        value={win}
                                        type="number"
                                        min="0"
                                        max="100"
                                    />
                                    <span
                                        className="number-input__arrow number-input__after"
                                        onClick={() => handleChange(setWin, 1, 0, 100)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                </div>
                                <div className="invalid-feedback">0 - 100</div>
                            </div>
                            {/* RRR */}
                            <div className="col-12 col-md-4 mt-2 mt-md-0">
                                <Tooltip title={t('tooltip_RRRInput')}>
                                    <label htmlFor="rrr" className="col-form-label">
                                        <span>RRR</span>
                                        <i className="bi bi-info-circle ms-1"></i>
                                    </label>
                                </Tooltip>
                                <div className="number-input__parent">
                                    <span
                                        className="number-input__arrow number-input__before"
                                        onClick={() => handleChange(setRRR, -1, 0, 100, 0.01)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                    <input
                                        className="form-control text-success number-input__input"
                                        value={rrr.toFixed(2)}
                                        data-decimals="2"
                                        onChange={e => setRRR(Number(e.target.value))}
                                    />
                                    <input
                                        name="rrr"
                                        className="number-input__hidden"
                                        value={rrr.toFixed(2)}
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="any"
                                        onChange={e => setRRR(Number(e.target.value))}
                                    />
                                    <span
                                        className="number-input__arrow number-input__after"
                                        onClick={() => handleChange(setRRR, 1, 0, 100, 0.01)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                </div>
                                <div className="invalid-feedback">{t('isrequired')}</div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            {/* Iterations */}
                            <div className="col-12 col-md-4">
                                <Tooltip title={t('tooltip_IterationsInput')}>
                                    <label htmlFor="iterations" className="col-form-label">
                                        <span>{t("Iterations")}</span>
                                        <i className="bi bi-info-circle ms-1"></i>
                                    </label>
                                </Tooltip>
                                <div className="number-input__parent">
                                    <span
                                        className="number-input__arrow number-input__before"
                                        onClick={() => handleChange(setIterations, -1, 1, 1000)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                    <input
                                        className="form-control text-success number-input__input"
                                        value={iterations}
                                        data-decimals="0"
                                        onChange={e => setIterations(Number(e.target.value))}
                                    />
                                    <input
                                        name="iterations"
                                        className="number-input__hidden"
                                        value={iterations}
                                        type="number"
                                        min="1"
                                        max="1000"
                                    />
                                    <span
                                        className="number-input__arrow number-input__after"
                                        onClick={() => handleChange(setIterations, 1, 1, 1000)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                </div>
                                <div className="invalid-feedback">{t("min")}. 1</div>
                            </div>
                            {/* Lines */}
                            <div className="col-12 col-md-4 mt-2 mt-md-0">
                                <Tooltip title={t('tooltip_LinesInput')}>
                                    <label htmlFor="lines" className="col-form-label">
                                        <span>{t("Lines")}</span>
                                        <i className="bi bi-info-circle ms-1"></i>
                                    </label>
                                </Tooltip>
                                <div className="number-input__parent">
                                    <span
                                        className="number-input__arrow number-input__before"
                                        onClick={() => handleChange(setLines, -1, 1, 100)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                    <input
                                        className="form-control text-success number-input__input"
                                        value={lines}
                                        data-decimals="0"
                                        onChange={e => setLines(Number(e.target.value))}
                                    />
                                    <input
                                        name="lines"
                                        className="number-input__hidden"
                                        value={lines}
                                        type="number"
                                        min="1"
                                        max="100"
                                    />
                                    <span
                                        className="number-input__arrow number-input__after"
                                        onClick={() => handleChange(setLines, 1, 1, 100)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                </div>
                                <div className="invalid-feedback">0 - 100</div>
                            </div>
                            {/* Risk percent */}
                            <div className="col-12 col-md-4 mt-2 mt-md-0">
                                <Tooltip title={t('tooltip_RiskPercentInput')}>
                                    <label htmlFor="lines" className="col-form-label">
                                        <span>{t('RiskPercent')}</span>
                                        <i className="bi bi-info-circle ms-1"></i>
                                    </label>
                                </Tooltip>
                                <div className="number-input__parent">
                                    <span
                                        className="number-input__arrow number-input__before"
                                        onClick={() => handleChange(setRisk, -1, 1, 100)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                    <input
                                        className="form-control text-success number-input__input"
                                        value={risk}
                                        data-decimals="0"
                                        onChange={e => setRisk(Number(e.target.value))}
                                    />
                                    <input
                                        name="rick"
                                        className="number-input__hidden"
                                        value={risk}
                                        type="number"
                                        min="1"
                                        max="100"
                                    />
                                    <span
                                        className="number-input__arrow number-input__after"
                                        onClick={() => handleChange(setRisk, 1, 1, 100)}
                                        style={{ cursor: 'pointer' }}
                                    ></span>
                                </div>
                                <div className="invalid-feedback">0 - 100</div>
                            </div>
                        </div>
                        <hr className="my-4 my-lg-5" />
                        <div className="row mb-lg-3">
                            <div className="col-12 col-md-4"></div>
                            <div className="col-12 col-md-4">
                                <button type="button" className="btn btn-primary btn--fluid" id="btn_run_equity_simulation" data-is-initialized="true" onClick={sendData}>
                                    <span className="equity-simulation__btn--compute">{t('Run')}</span>
                                    <span className="equity-simulation__btn--loading d-none"> {t("Running")} <div style={{ color: "white", backgroundColor: "#0781fe" }}></div> </span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {isShowRun && avgDataTable && equityCurves && (<>
                <div className="row mt-4" id="equity_simulation_header" style={{ display: 'block' }}>
                    <div className="col-12">
                        <h6>{t('TradingAccounts')}</h6>
                    </div>
                </div>

                <div id="equity_simulation_div" style={{ display: 'block' }}>
                    <div className="portlet equity-simulation__chart">
                        <div className="portlet__body" style={{ padding: '0' }}>
                            <div className="row">
                                <div className="col-12">
                                    <div className="text-center">
                                        <TradingChart equityCurves={equityCurves} avgDataTable={avgDataTable} />
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="text-center">
                                        The black line resembles the average of result curves.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="portlet mt-4 p-0">
                    <div className="portlet__body pt-0">
                        <div id="equity_simulation_table">
                            <div className="equity-simulation__table">
                                <div className="">
                                    <div className="equity-simulation__row">
                                        <div className="equity-simulation__col">
                                            <Tooltip title={t('tooltip_minequity')}>
                                                <div className="text-primary">
                                                    <span>{t('Minequity')}</span>
                                                    <i className="bi bi-info-circle ms-1"></i>
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div className="equity-simulation__col bg--mild">{Number(avgDataTable.min_equity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                    </div>
                                    <div className="equity-simulation__row">
                                        <div className="equity-simulation__col">
                                            <Tooltip title={t('tooltip_max_equity')}>
                                                <div className="text-primary">
                                                    <span>{t('Maxequity')}</span>
                                                    <i className="bi bi-info-circle ms-1"></i>
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div className="equity-simulation__col bg--mild">{Number(avgDataTable.max_equity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                    </div>
                                    <div className="equity-simulation__row">
                                        <div className="equity-simulation__col">
                                            <Tooltip title={t('tooltip_MaxConsecutiveWinners')}>
                                                <div className="text-primary">
                                                    <span>{t('MaxConsecutiveWinners')}</span>
                                                    <i className="bi bi-info-circle ms-1"></i>
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div className="equity-simulation__col bg--mild">{avgDataTable.max_consecutive_winners}</div>
                                    </div>
                                    <div className="equity-simulation__row">
                                        <div className="equity-simulation__col">
                                            <Tooltip title={t('tooltip_MaxConsecutiveLosers')}>
                                                <div className="text-primary">
                                                    <span>{t('MaxConsecutiveLosers')}</span>
                                                    <i className="bi bi-info-circle ms-1"></i>
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div className="equity-simulation__col bg--mild">{avgDataTable.max_consecutive_losers}</div>
                                    </div>
                                </div>
                                <div className="">
                                    <div className="equity-simulation__row">
                                        <div className="equity-simulation__col">
                                            <Tooltip title={t('tooltip_MaxDrawdown')}>
                                                <div className="text-primary">
                                                    <span>{t('MaxDrawdown')}</span>
                                                    <i className="bi bi-info-circle ms-1"></i>
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div className="equity-simulation__col bg--mild">{avgDataTable.max_drawdown_percent}%</div>
                                    </div>
                                    <div className="equity-simulation__row">
                                        <div className="equity-simulation__col">
                                            <Tooltip title={t('tooltip_Avgdrawdown')}>
                                                <div className="text-primary">
                                                    <span>{t('Avgdrawdown')}</span>
                                                    <i className="bi bi-info-circle ms-1"></i>
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div className="equity-simulation__col bg--mild">{avgDataTable.avg_drawdown_percent}%</div>
                                    </div>
                                    <div className="equity-simulation__row">
                                        <div className="equity-simulation__col">
                                            <Tooltip title={t('tooltip_Avgmaxdrawdown')}>
                                                <div className="text-primary">
                                                    <span>{t('Avgmaxdrawdown')}.</span>
                                                    <i className="bi bi-info-circle ms-1"></i>
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div className="equity-simulation__col bg--mild">10.12%</div>
                                    </div>
                                    <div className="equity-simulation__row d-none d-xl-grid">
                                        <div className="equity-simulation__col">&nbsp;</div>
                                        <div className="equity-simulation__col bg--mild">&nbsp;</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>)}

            <div className="footer mb-5 mb-md-auto">
                <div className="row my-4 small">
                    <div className="col-12 small text-center text-md-end mb-md-1 mb-2 fw-bold">
                        <a href="#" id="ftmo-cookie-settings" className=""> {t('CookieSettings')} </a>
                        &nbsp;
                        <a href="#" data-bs-toggle="modal" data-bs-target="#modal_privacy_policy"> {t('PrivacyPolicy')} </a>
                        &nbsp;
                        <a href="#" id="footer__link-terms-conditions"> {t('TermsConditions')} </a>
                    </div>

                    <div className="col-12 small">
                        <p>
                            {t('disclaimerFull')}
                        </p>
                    </div>
                </div>
            </div>
        </Layout>
    )
}
export default Dashboard